import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import type { PanelKind } from "./editor/panelDefinitions.ts";
import type { SeriesSelection } from "./mapModes.ts";
import type { ResultLevel } from "./resultSelection.ts";
import type { State } from "./appState.ts";

/**
 * Which left-hand panel (editor/calibration/bulk AEMET) or workspace view
 * (map/cards) is showing, plus the map/card selection that lives underneath
 * them — grouped together because every panel switch below deliberately
 * clears `mapSelection`/`seriesSelection`/`mitecoMode` (an in-progress
 * selection stops meaning anything once you're looking at a different
 * panel), so the "which panel" and "what's selected in it" concerns are
 * genuinely coupled, not just colocated. The state transitions are pulled
 * out as named, exported functions (rather than staying as inline
 * `setState` closures) so they're directly unit-testable without a React
 * environment — see project_evalhid_code_audit.md for the ongoing App.tsx
 * breakup this is part of.
 */
export function withResultsPanelToggled(current: State): State {
  return current.phase === "loaded"
    ? { ...current, resultsPanelOpen: !current.resultsPanelOpen }
    : current;
}

export function withEditorPanelToggled(current: State): State {
  return current.phase === "loaded"
    ? {
        ...current,
        editorPanelOpen: !current.editorPanelOpen,
        // Same left-hand slot as the calibration panel — see its own comment.
        calibrationPanelOpen: false,
        // Closing (or reopening) the edit bar makes the selection meaningless.
        mapSelection: new Set(),
        seriesSelection: null,
        mitecoMode: null,
      }
    : current;
}

/** CalibrationPanel.tsx — same left-hand slot as the editor panel (mutually exclusive, see State.calibrationPanelOpen's own comment). */
export function withCalibrationPanelToggled(current: State): State {
  return current.phase === "loaded"
    ? {
        ...current,
        calibrationPanelOpen: !current.calibrationPanelOpen,
        editorPanelOpen: false,
        mapSelection: new Set(),
        seriesSelection: null,
        mitecoMode: null,
      }
    : current;
}

// Closes the editor panel (same side-clearing as withEditorPanelToggled's
// closing branch) and opens the bulk AEMET dialog in the same state update —
// see EditSubBasinDialog.tsx's own AEMET button for why the two don't stay
// stacked together. `onlyCodes` pre-scopes the dialog to those sub-basins
// (SeriesPanel.tsx's "gap left over from an extend" notice); omitted (or
// undefined) for the normal "whole project" open.
export function withBulkAemetFetchOpened(current: State, onlyCodes?: number[]): State {
  return current.phase === "loaded"
    ? {
        ...current,
        editorPanelOpen: false,
        calibrationPanelOpen: false,
        bulkAemetOpen: true,
        bulkAemetOnlyCodes: onlyCodes ?? null,
        mapSelection: new Set(),
        seriesSelection: null,
        mitecoMode: null,
      }
    : current;
}

export function withBulkAemetFetchClosed(current: State): State {
  return current.phase === "loaded"
    ? { ...current, bulkAemetOpen: false, bulkAemetOnlyCodes: null }
    : current;
}

// Only opens (doesn't toggle) — TopBar's pencil menu always wants a
// specific catalog visible, never "close whatever's open". Same
// side-clearing as withEditorPanelToggled's opening branch. A no-op if the
// editor panel is already open (only `activePanel`, owned locally by the
// hook, needs to change in that case).
export function withEditorPanelOpened(current: State): State {
  return current.phase === "loaded" && !current.editorPanelOpen
    ? {
        ...current,
        editorPanelOpen: true,
        calibrationPanelOpen: false,
        mapSelection: new Set(),
        seriesSelection: null,
        mitecoMode: null,
      }
    : current;
}

export function withMapSelection(current: State, selection: Set<number>): State {
  return current.phase === "loaded" ? { ...current, mapSelection: selection } : current;
}

export function withSeriesSelection(current: State, selection: SeriesSelection): State {
  return current.phase === "loaded" ? { ...current, seriesSelection: selection } : current;
}

export function withMapViewToggled(current: State): State {
  return current.phase === "loaded" ? { ...current, mapView: !current.mapView } : current;
}

/** Unlike `withMapViewToggled`, unconditionally forces map view ON — SubBasinSearch.tsx picking a result needs a real map to pan/highlight on, whichever view was showing before (cards or map). */
export function withMapViewOn(current: State): State {
  return current.phase === "loaded" ? { ...current, mapView: true } : current;
}

export function usePanels(setState: Dispatch<SetStateAction<State>>) {
  const toggleResultsPanel = useCallback(() => {
    setState(withResultsPanelToggled);
  }, [setState]);

  const toggleEditorPanel = useCallback(() => {
    setState(withEditorPanelToggled);
  }, [setState]);

  const toggleCalibrationPanel = useCallback(() => {
    setState(withCalibrationPanelToggled);
  }, [setState]);

  const openBulkAemetFetch = useCallback(
    (onlyCodes?: number[]) => {
      setState((current) => withBulkAemetFetchOpened(current, onlyCodes));
    },
    [setState]
  );

  const closeBulkAemetFetch = useCallback(() => {
    setState(withBulkAemetFetchClosed);
  }, [setState]);

  // Which catalog to show in EditBar (null = none) — lives here, not
  // inside EditBar, so the pencil menu (TopBar.tsx) can set it directly
  // instead of always opening a blank panel that then has to be navigated
  // with the tab bar.
  const [activePanel, setActivePanel] = useState<PanelKind>(null);

  // Which of the two assignment tables Step2Zones.tsx is showing (zone vs.
  // snow zone) — lives here (not as Step2Zones's own local state, its
  // default when used uncontrolled e.g. from the wizard) so App.tsx can
  // read it too, to know which field to color sub-basins by on the
  // map/cards while that tab is open (see assignmentColors.ts).
  const [zoneAssignMode, setZoneAssignMode] = useState<"zone" | "snow">("zone");

  // Which level tab CalculationResults.tsx is currently showing (Outlet
  // point/Zone/Sub-basin/Aquifer) — lives here, not as that component's own
  // state alone (it still owns the actual state, this is just a mirror it
  // reports up to on every change, see its own `onLevelChange`), so App.tsx
  // can read it too: while viewing the "Outlet point"/"Zone" level, the map
  // colors sub-basins by that same field (assignmentColors.ts), same idea
  // as `zoneAssignMode` above for the editor's own bulk-assign tabs.
  const [resultsLevel, setResultsLevel] = useState<ResultLevel>("outletPoint");

  const openEditorPanel = useCallback(
    (panel: PanelKind) => {
      setActivePanel(panel);
      setState(withEditorPanelOpened);
    },
    [setState]
  );

  const changeMapSelection = useCallback(
    (selection: Set<number>) => setState((current) => withMapSelection(current, selection)),
    [setState]
  );

  const selectSeries = useCallback(
    (selection: SeriesSelection) => setState((current) => withSeriesSelection(current, selection)),
    [setState]
  );

  const toggleMapView = useCallback(() => {
    setState(withMapViewToggled);
  }, [setState]);

  // Which sub-basin SubBasinSearch.tsx (TopBar.tsx) last picked — purely a
  // "look, it's over there" locator, so it lives here as plain UI state
  // (like activePanel above), not in appState.ts's persisted State: there's
  // nothing worth restoring after a reload, and SubBasinMap.tsx only reads
  // it to pan/highlight, never to gate any real behavior.
  const [highlightedSubBasin, setHighlightedSubBasin] = useState<number | null>(null);

  const selectSubBasinFromSearch = useCallback(
    (subBasinCode: number) => {
      setState(withMapViewOn);
      setHighlightedSubBasin(subBasinCode);
    },
    [setState]
  );

  return {
    activePanel,
    setActivePanel,
    zoneAssignMode,
    setZoneAssignMode,
    resultsLevel,
    setResultsLevel,
    openEditorPanel,
    toggleResultsPanel,
    toggleEditorPanel,
    toggleCalibrationPanel,
    openBulkAemetFetch,
    closeBulkAemetFetch,
    changeMapSelection,
    selectSeries,
    toggleMapView,
    highlightedSubBasin,
    selectSubBasinFromSearch,
  };
}
